import { t } from 'i18next';

import { toast } from '@/components/ui/toast';

import {
  WorkflowStatusToast,
  type WorkflowTrackingOptions,
} from './WorkflowStatusToast';
import { WorkflowToastContent } from './WorkflowToastContent';

type WorkflowToastOptions = {
  title: string;
  errorTitle?: string;
};

export type WorkflowToast = {
  success: (message: string, title: string) => void;
  error: (message: string, title?: string) => void;
  cancelled: () => void;
  track: (options: WorkflowTrackingOptions) => void;
};

export function createWorkflowToast({
  title,
  errorTitle = t('workflow.toast.failedTitleGeneric'),
}: WorkflowToastOptions): WorkflowToast {
  // The global toaster owns this notification, independent of the launching page.
  // Its loading variant supplies the spinner and prevents manual dismissal.
  const id = toast.add({
    data: { variant: 'loading' },
    title,
    description: (
      <WorkflowToastContent
        progress={null}
        label={t('workflow.toast.submitting')}
      />
    ),
    timeout: 0,
  });

  const notification: WorkflowToast = {
    track(options) {
      toast.update(id, {
        description: (
          <WorkflowStatusToast {...options} notification={notification} />
        ),
        timeout: 0,
      });
    },

    success(message: string, title: string) {
      toast.update(id, {
        data: { variant: 'success' },
        title,
        description: message,
        timeout: 3000,
      });
    },

    error(message: string, title = errorTitle) {
      toast.update(id, {
        data: { variant: 'error' },
        title,
        description: message,
        timeout: 5000,
      });
    },

    cancelled() {
      toast.update(id, {
        data: { variant: 'info' },
        title: t('workflow.toast.cancelledTitle'),
        description: t('workflow.toast.cancelledDetail'),
        timeout: 3000,
      });
    },
  };

  return notification;
}
